import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMFOItems } from "@/hooks/use-mfo-items";
import { useDepartments } from "@/hooks/use-departments";
import { useGetEmployees } from "@/hooks/use-employees";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import CreateMFOModal from "@/components/mfo/CreateMFOModal";
import { MFOItem } from "@/types/kanban";
import { Link } from "react-router-dom";

const EditMFO = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getMFODetail } = useMFOItems();
  const { departments } = useDepartments();
  const { data: employees = [] } = useGetEmployees();
  const [mfo, setMfo] = useState<MFOItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchMFO = async () => {
      if (!id) return;
      
      try {
        const data = await getMFODetail(id);
        if (data) {
          setMfo(data);
          setIsModalOpen(true);
        } else {
          toast({
            title: "Error",
            description: "MFO not found",
            variant: "destructive",
          });
          navigate("/dashboard/mfo");
        }
      } catch (error) {
        console.error("Error fetching MFO:", error);
        toast({
          title: "Error",
          description: "Failed to load MFO details",
          variant: "destructive",
        });
        navigate("/dashboard/mfo");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMFO();
  }, [id, getMFODetail, navigate, toast]);

  const handleSave = async (updatedMFO: MFOItem) => {
    try {
      toast({
        title: "Success",
        description: "MFO updated successfully",
      });
      navigate(`/dashboard/mfo/${id}`);
    } catch (error) {
      console.error("Error updating MFO:", error);
      toast({
        title: "Error",
        description: "Failed to update MFO",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-xl">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="mr-2"
          onClick={() => navigate(`/dashboard/mfo/${id}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to MFO Details
        </Button>
      </div>

      {mfo && (
        <CreateMFOModal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          mode="edit"
          initialMFO={mfo}
          departments={departments}
          employees={employees}
          onSave={handleSave}
          onSuccess={() => navigate(`/dashboard/mfo/${id}`)}
        />
      )}

      <Link to={`/dashboard/mfo/${mfo.id}/edit`}>Edit MFO</Link>
    </div>
  );
};

export default EditMFO; 